import React from 'react';
import { RoomSessionValue } from '../../../connection/health';
import {
	Probe, ProbeStatus, PROBE_KEYS, RoomHealth,
} from '../types';
import HealthPanel from '../health-panel';
import CopyValue from '../copy-value';
import StatusPill from '../status-pill';
import { liveMonitorUrl } from '../format';

interface Props {
	probe: Probe<RoomSessionValue> | undefined;
	health: RoomHealth | null;
	eventUuid?: string | null;
}

const SESSION_STATUS_TO_PROBE: Record<string, ProbeStatus> = {
	started: ProbeStatus.Ok,
	starting: ProbeStatus.Degraded,
	closing: ProbeStatus.Degraded,
	none: ProbeStatus.Empty,
};

const PROBE_LABELS: Record<string, string> = {
	roomSession: 'room session',
	opentok: 'opentok session',
	broadcast: 'broadcast',
	connectedUsers: 'connected users',
};

/**
 * Sync's room session entity - the record that gates OpenTok and LiveKit
 * provisioning - alongside the round trip time of every probe in this snapshot,
 * which is the closest thing to a service latency reading the client can take.
 */
export default function RoomSessionPanel({ probe, health, eventUuid }: Props) {
	const session = probe?.data;

	// The one view of what viewers actually received, rather than what we sent.
	const muxUrl = liveMonitorUrl(eventUuid);

	return (
		<HealthPanel title='Room session' source='sync' probe={probe}>
			<dl className='health-facts'>
				<dt>State</dt>
				<dd>
					<StatusPill
						status={SESSION_STATUS_TO_PROBE[session?.status ?? 'none'] ?? ProbeStatus.Empty}
						label={session?.status ?? 'unknown'}
					/>
				</dd>
				<dt>Session</dt>
				<dd><CopyValue value={session?.sessionId ?? ''} mono /></dd>
				<dt>Room</dt>
				<dd>{session?.room ?? '—'}</dd>
				{!!muxUrl && (
					<>
						<dt>Viewer playback</dt>
						<dd>
							<a
								className='health-external-link'
								href={muxUrl}
								target='_blank'
								rel='noreferrer'
							>
								Mux analytics ↗
							</a>
						</dd>
					</>
				)}
			</dl>

			<table className='health-table'>
				<thead>
					<tr>
						<th>Probe</th>
						<th>HTTP</th>
						<th>Latency</th>
					</tr>
				</thead>
				<tbody>
					{PROBE_KEYS.map((key) => {
						const result = health?.[key];
						return (
							<tr key={key}>
								<td>{PROBE_LABELS[key]}</td>
								<td className='health-mono'>{result?.httpStatus || '—'}</td>
								<td className='health-mono'>{result ? `${result.latencyMs}ms` : '—'}</td>
							</tr>
						);
					})}
				</tbody>
			</table>
		</HealthPanel>
	);
}
